import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { TextPromptDialog } from '@/components/ui/TextPromptDialog';
import { api, ApiError } from '@/lib/api/client';
import { branchKey, keys } from '@/lib/queryKeys';
import { toast } from '@/hooks/useToast';
import { useAuth } from '@/hooks/useAuth';

interface AdjustmentRequest {
  id: number;
  inventory_id: number;
  adjustment_type: string;
  quantity_delta: number;
  reason: string;
  value_at_cost: string | null;
  requested_by: string | null;
  requested_by_user_id: number | null;
  requested_at: string;
  generic_name: string;
  strength: string | null;
  batch_number: string;
  quantity_available: number;
  store_name: string | null;
}

const listKey = () => branchKey('pharmacy', 'adjustment-requests');

/**
 * Stock adjustments waiting for approval (Phase 1 G15), above the Inventory
 * table. An approver approves or rejects (never their own); the person who
 * asked can withdraw. Renders nothing when there's nothing waiting.
 */
export function AdjustmentRequestsPanel() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const canApprove = !!user?.modules?.includes('pharmacy:approve-adjustment');
  const query = useQuery({
    queryKey: listKey(),
    queryFn: () => api.get<AdjustmentRequest[]>('/pharmacy/adjustment-requests', { query: { status: 'pending' } }),
    refetchInterval: 30_000,
  });
  const [rejecting, setRejecting] = useState<AdjustmentRequest | null>(null);
  const [rejectError, setRejectError] = useState<string | null>(null);

  const done = (msg: string) => {
    toast({ title: msg, variant: 'success' });
    qc.invalidateQueries({ queryKey: listKey() });
    qc.invalidateQueries({ queryKey: keys.pharmacy.inventory() });
  };
  const failToast = (e: unknown) => toast({ title: 'Not done', description: e instanceof ApiError || e instanceof Error ? e.message : String(e), variant: 'danger' });
  const approve = useMutation({ mutationFn: (id: number) => api.post(`/pharmacy/adjustment-requests/${id}/approve`), onSuccess: () => done('Approved — stock updated'), onError: failToast });
  const withdraw = useMutation({ mutationFn: (id: number) => api.post(`/pharmacy/adjustment-requests/${id}/cancel`), onSuccess: () => done('Request withdrawn'), onError: failToast });
  const reject = useMutation({
    mutationFn: ({ id, note }: { id: number; note: string }) => api.post(`/pharmacy/adjustment-requests/${id}/reject`, { note }),
    onSuccess: () => { setRejecting(null); done('Rejected — stock unchanged'); },
    onError: (e) => setRejectError(e instanceof ApiError || e instanceof Error ? e.message : 'Not saved'),
  });

  const rows = query.data ?? [];
  if (!rows.length) return null;
  return (
    <div className="mb-3 rounded-[var(--radius)] border border-warn/40 bg-warn-soft px-3 py-2">
      <div className="mb-1.5 text-[12.5px] font-semibold text-warn-ink">{rows.length} stock adjustment{rows.length === 1 ? '' : 's'} waiting for approval</div>
      <ul className="divide-y divide-warn/20 text-[12px]">
        {rows.map((r) => {
          const mine = r.requested_by_user_id != null && r.requested_by_user_id === user?.id;
          return (
            <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 py-1.5">
              <span className="text-ink">
                <b>{r.quantity_delta > 0 ? '+' : ''}{r.quantity_delta}</b> × {r.generic_name}{r.strength ? ` ${r.strength}` : ''} · batch {r.batch_number}
                {r.store_name ? ` · ${r.store_name}` : ''} · {r.adjustment_type.replace(/_/g, ' ')}
                <span className="text-ink-muted"> — “{r.reason}” · {r.requested_by}{r.value_at_cost ? ` · ₹${Number(r.value_at_cost).toFixed(2)} at cost` : ''} · now {r.quantity_available} in stock</span>
              </span>
              <span className="flex gap-1.5">
                {canApprove && !mine && (
                  <>
                    <Button size="sm" onClick={() => approve.mutate(r.id)} loading={approve.isPending && approve.variables === r.id}>Approve</Button>
                    <Button size="sm" variant="ghost" onClick={() => { setRejectError(null); setRejecting(r); }}>Reject…</Button>
                  </>
                )}
                {mine && <Button size="sm" variant="ghost" onClick={() => withdraw.mutate(r.id)}>Withdraw</Button>}
                {!canApprove && !mine && <span className="text-ink-muted">Waiting</span>}
              </span>
            </li>
          );
        })}
      </ul>
      <TextPromptDialog
        open={!!rejecting}
        title="Reject this adjustment?"
        description="Stock stays as it is. The person who asked sees your reason."
        label="Reason"
        confirmLabel="Reject"
        danger
        pending={reject.isPending}
        error={rejectError}
        onClose={() => setRejecting(null)}
        onConfirm={(note) => rejecting && reject.mutate({ id: rejecting.id, note })}
      />
    </div>
  );
}
